import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  alpha,
} from '@mui/material';
import {
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  MenuBook,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

function StudentResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [studentInfo, setStudentInfo] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [openSubjectDialog, setOpenSubjectDialog] = useState(false);
  const [studentSubjects, setStudentSubjects] = useState([]);

  useEffect(() => {
    fetchResults();
  }, []);

  useEffect(() => {
    // Show subject selection dialog when student has subjects and hasn't selected yet
    if (studentSubjects.length > 0 && !selectedSubject) {
      setOpenSubjectDialog(true);
    }
  }, [studentSubjects, selectedSubject]);

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setOpenSubjectDialog(false);
    fetchResults(subject);
  };

  const handleChangeSubject = () => {
    setSelectedSubject('');
    setOpenSubjectDialog(true);
  };

  const fetchResults = async (subject = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (subject) {
        params.subject = subject;
      }
      
      // Use secure endpoint that only returns authenticated student's results
      const response = await axios.get(
        `${API_URL}/my-results`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setStudentInfo(response.data.student);
      setResults(response.data.results);
      
      // Get student subjects (only on first load)
      if (response.data.student?.subjects && studentSubjects.length === 0) {
        const subjects = typeof response.data.student.subjects === 'string'
          ? JSON.parse(response.data.student.subjects)
          : response.data.student.subjects;
        setStudentSubjects(Array.isArray(subjects) ? subjects : []);
      }
    } catch (err) {
      setError('Failed to fetch results');
    } finally {
      setLoading(false);
    }
  };

  // Results are already grouped by exam from the backend
  const groupedResults = results;

  // Calculate statistics
  const totalExams = results.length;
  const totalSubjects = results.reduce((sum, examGroup) => sum + examGroup.subjects.length, 0);
  const passedSubjects = results.reduce((sum, examGroup) => {
    return sum + examGroup.subjects.filter(s => !s.is_absent && s.grade !== 'F').length;
  }, 0);
  
  const averagePercentage = totalSubjects > 0
    ? results.reduce((sum, examGroup) => {
        return sum + examGroup.subjects.reduce((subSum, subject) => {
          return subSum + (!subject.is_absent ? subject.percentage : 0);
        }, 0);
      }, 0) / totalSubjects
    : 0;

  const getGradeColor = (grade) => {
    if (grade === 'A') return 'success';      // Over 75
    if (grade === 'B') return 'primary';      // 65-74
    if (grade === 'C') return 'warning';      // 50-64
    if (grade === 'S') return 'warning';      // 35-49
    return 'error';                           // Below 35 (F)
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <LinearProgress sx={{ width: 300 }} />
        {selectedSubject && (
          <Typography variant="body2" sx={{ color: '#fff' }}>
            Loading {selectedSubject} results...
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  mr: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <SchoolIcon sx={{ fontSize: 35 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3748' }}>
                  My Exam Results
                </Typography>
                {studentInfo && (
                  <Typography variant="body1" sx={{ color: '#718096' }}>
                    {studentInfo.name_with_initials} - {studentInfo.student_id}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Subject Filter Banner */}
        {selectedSubject && (
          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              mb: 3,
              background: 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(5,150,105,0.08) 100%)',
              backdropFilter: 'blur(10px)',
              border: '2px solid #10b981',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  bgcolor: '#10b981',
                  color: 'white',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MenuBook sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#065f46' }}>
                    Viewing: {selectedSubject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#047857' }}>
                    Results filtered by {selectedSubject}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleChangeSubject}
                sx={{
                  borderColor: '#10b981',
                  color: '#065f46',
                  '&:hover': {
                    borderColor: '#059669',
                    bgcolor: alpha('#10b981', 0.08),
                  }
                }}
              >
                Change Subject
              </Button>
            </Box>
          </Paper>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AssessmentIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Exams</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {totalExams}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                color: '#fff',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrophyIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Passed</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {passedSubjects}/{totalSubjects}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
                color: '#fff',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Average</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {averagePercentage.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
                color: '#fff',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SchoolIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Subjects</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {totalSubjects}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Results by Exam */}
        {groupedResults.length === 0 ? (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
            }}
          >
            <SchoolIcon sx={{ fontSize: 80, color: '#cbd5e0', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#718096' }}>
              No exam results available yet
            </Typography>
          </Paper>
        ) : (
          groupedResults.map(({ exam, subjects, total_marks, marks_obtained }) => (
            <Paper
              key={exam.id}
              elevation={3}
              sx={{
                p: 3,
                mb: 3,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3748', mb: 1 }}>
                  {exam.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#718096' }}>
                  {exam.grade} • Date: {exam.exam_date}
                </Typography>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: '#f7fafc' }}>
                        Subject
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: '#f7fafc' }}>
                        Marks Obtained
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: '#f7fafc' }}>
                        Grade
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: '#f7fafc' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subjects.map((subject) => (
                      <TableRow
                        key={subject.id}
                        sx={{ '&:hover': { backgroundColor: '#f7fafc' } }}
                      >
                        <TableCell>{subject.subject}</TableCell>
                        <TableCell>
                          {subject.is_absent ? (
                            <Chip label="AB" color="error" size="small" />
                          ) : (
                            `${subject.marks_obtained} / ${subject.total_marks}`
                          )}
                        </TableCell>
                        <TableCell>
                          {subject.grade && !subject.is_absent ? (
                            <Chip
                              label={subject.grade}
                              color={getGradeColor(subject.grade)}
                              size="small"
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {subject.is_absent ? (
                            <Chip label="Absent" color="error" size="small" />
                          ) : subject.grade === 'F' ? (
                            <Chip label="Failed" color="error" size="small" />
                          ) : (
                            <Chip label="Passed" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))
        )}

        {/* Subject Selection Dialog */}
        <Dialog 
          open={openSubjectDialog} 
          onClose={() => {}} 
          maxWidth="sm" 
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="700" sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Select Your Subject
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                Choose a subject to view your results
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {studentSubjects.map((subject) => (
                <Paper
                  key={subject}
                  onClick={() => handleSubjectSelect(subject)}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    border: '2px solid #e2e8f0',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: subject === 'Mathematics' ? '#667eea' : '#10b981',
                      bgcolor: subject === 'Mathematics' ? alpha('#667eea', 0.05) : alpha('#10b981', 0.05),
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      bgcolor: subject === 'Mathematics' ? alpha('#667eea', 0.15) : alpha('#10b981', 0.15),
                      color: subject === 'Mathematics' ? '#667eea' : '#10b981',
                      borderRadius: 2,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MenuBook sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="700" sx={{ 
                        color: '#2d3748',
                        mb: 0.5
                      }}>
                        {subject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#718096' }}>
                        {subject === 'Mathematics' 
                          ? 'View your mathematics exam results' 
                          : 'View your science exam results'}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      color: subject === 'Mathematics' ? '#667eea' : '#10b981',
                      fontSize: 32
                    }}>
                      →
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
            {studentSubjects.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <MenuBook sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                  No subjects assigned
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                  Please contact your administrator
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
}

export default StudentResults;
